/**
 * The one place that talks to the API.
 *
 * ## Where the token lives
 *
 * In memory, with the refresh token in `localStorage`. The usual objection is
 * that `localStorage` is readable by any script on the page, which is true —
 * but the alternative everyone reaches for, an httpOnly cookie, is not
 * available to us: the dashboard is a static site on a different origin from
 * the API, so a cookie would have to be `SameSite=None; Secure` and sent
 * cross-site on every request, which is a CSRF surface that then needs its own
 * defence. Given the choice between "XSS can read the token" and "XSS can read
 * the token *and* every other origin can forge requests", this is the smaller
 * one.
 *
 * The access token is kept in a module variable rather than in storage, so the
 * short-lived credential is not persisted at all; only the refresh token is,
 * and redeeming it rotates it.
 *
 * ## Refresh
 *
 * A 401 triggers exactly one refresh attempt, and concurrent 401s share it.
 * Without the sharing, a dashboard screen that fires five queries at once on a
 * stale token would redeem the refresh token five times — and since redemption
 * rotates it, four of those would be rejected as replays and log the user out
 * during an ordinary page load.
 */

import type { SignupAttribution } from "../auth/attribution";
import type { User } from "./types";

const TOKEN_KEY = "bozorbot:refresh";

/** Same-origin in production and in dev (Vite proxies /api). */
export const API_BASE = import.meta.env.VITE_API_URL ?? "";

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

/** Notified when the session ends, so the router can send the user to /login. */
type SessionListener = () => void;
const sessionEnded = new Set<SessionListener>();

export function onSessionEnded(listener: SessionListener): () => void {
  sessionEnded.add(listener);
  return () => sessionEnded.delete(listener);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** RFC 9457 problem details, when the API sent them. */
    readonly detail?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

function readRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Storage disabled. The session works until the tab closes.
    return null;
  }
}

function writeRefreshToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* see readRefreshToken */
  }
}

export function setSession(access: string, refresh: string): void {
  accessToken = access;
  writeRefreshToken(refresh);
}

export function clearSession(): void {
  accessToken = null;
  writeRefreshToken(null);
}

export function hasStoredSession(): boolean {
  return Boolean(readRefreshToken());
}

export function currentAccessToken(): string | null {
  return accessToken;
}

/**
 * Exchange the stored refresh token for a new pair.
 *
 * Shared between concurrent callers — see this module's docstring for why that
 * matters more than it looks.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const token = readRefreshToken();
  if (!token) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/auth/jwt/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: token }),
      });

      if (!response.ok) {
        clearSession();
        return false;
      }

      const body = (await response.json()) as {
        access_token: string;
        refresh_token: string;
      };
      setSession(body.access_token, body.refresh_token);
      return true;
    } catch {
      // A network failure is not a bad token. Leave the stored token alone so
      // the next attempt can still succeed once connectivity returns.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Form-encoded, for the OAuth2 password flow the login endpoint expects. */
  form?: Record<string, string>;
  signal?: AbortSignal;
  /** Internal: prevents a refresh loop. */
  retry?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (options.form) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    body = new URLSearchParams(options.form).toString();
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    body,
    signal: options.signal ?? null,
    // No cookies: the API's CORS policy does not allow credentials, and the
    // bearer token above is the whole of the authentication story.
    credentials: "omit",
  });

  if (response.status === 401 && !options.retry) {
    if (await refreshSession()) {
      return request<T>(path, { ...options, retry: true });
    }
    clearSession();
    for (const listener of sessionEnded) listener();
    throw new ApiError(401, "Your session has expired. Please sign in again.");
  }

  if (!response.ok) throw await problem(response);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function problem(response: Response): Promise<ApiError> {
  let detail: unknown;
  let message = `Request failed (${response.status})`;

  try {
    detail = await response.json();
    const body = detail as { detail?: unknown; title?: string };

    if (typeof body.detail === "string") {
      message = body.detail;
    } else if (Array.isArray(body.detail)) {
      // FastAPI's validation errors. Rendering the raw array at a user is
      // hostile; the first message with its field is the useful part.
      const first = body.detail[0] as { loc?: unknown[]; msg?: string } | undefined;
      const field = Array.isArray(first?.loc) ? first.loc.at(-1) : undefined;
      message = field ? `${String(field)}: ${first?.msg ?? "is invalid"}` : (first?.msg ?? message);
    } else if (body.title) {
      message = body.title;
    }
  } catch {
    /* an error page from a proxy, not from us */
  }

  return new ApiError(response.status, message, detail);
}

/**
 * Fetch a file rather than JSON.
 *
 * The data export is streamed NDJSON behind a bearer token, so it cannot be a
 * plain `<a href>` — a link carries no Authorization header. It has to be
 * fetched and handed to the browser as a blob.
 *
 * `request` is not reusable here: it parses the body as JSON, which for a
 * hundred-megabyte export would mean materialising the whole thing as a
 * string and then again as an object. This reads the response as a blob, which
 * the browser can stream to disk.
 */
export async function download(path: string, retry = false): Promise<DownloadedFile> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${API_BASE}${path}`, { headers, credentials: "omit" });

  if (response.status === 401 && !retry && (await refreshSession())) {
    return download(path, true);
  }

  if (!response.ok) throw await problem(response);

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("content-disposition")) ?? "export.ndjson",
  };
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

/** Pull the server's suggested name out of `Content-Disposition`. */
function filenameFrom(header: string | null): string | null {
  // `match[1]` is typed as possibly-undefined under `noUncheckedIndexedAccess`,
  // and a capture group that matched always has a value — but the compiler
  // cannot know that, so the fallback is explicit rather than asserted.
  const match = header?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? null;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  download,
};

/**
 * Sign in, exchanging credentials for a token pair.
 *
 * The login endpoint is fastapi-users' OAuth2 password flow, which is
 * form-encoded and names the email field `username`. That is a detail of the
 * spec, not of our API, and it stops here.
 */
export async function login(email: string, password: string): Promise<User> {
  const tokens = await request<{ access_token: string; refresh_token: string }>(
    "/api/v1/auth/jwt/login",
    { method: "POST", form: { username: email, password } }
  );
  setSession(tokens.access_token, tokens.refresh_token);
  return api.get<User>("/api/v1/users/me");
}

export async function register(
  email: string,
  password: string,
  attribution: SignupAttribution = {}
): Promise<User> {
  await request("/api/v1/auth/register", {
    method: "POST",
    body: { email, password, signup_attribution: attribution },
  });
  return login(email, password);
}

/** Answers 202 whether or not the address has an account — see the page. */
export async function requestPasswordReset(email: string): Promise<void> {
  await request("/api/v1/auth/forgot-password", { method: "POST", body: { email } });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await request("/api/v1/auth/reset-password", { method: "POST", body: { token, password } });
}

/**
 * Adopt an impersonation token.
 *
 * Deliberately does not touch the stored refresh token: when the staff member
 * closes the tab, the impersonated session ends with it, and their own session
 * is still there. A "stop impersonating" button that could not restore the
 * original session would make this a trap rather than a tool.
 */
export function beginImpersonation(access: string): void {
  accessToken = access;
}
